/**
 * Right-column price breakdown for the booking review page.
 *
 * VAT-inclusive: `b.total` is the customer-payable total. VAT is shown
 * as an informational line (never added on top). Advance deposit +
 * balance-due-at-pickup breakdown surfaces the two payment amounts.
 */
export default function PriceBreakdown({ b }) {
  const fs = b.financial_summary || {};
  const vatRate = fs.vat_rate_pct ?? 24;
  const netTotal = b.net_total ?? fs.net_total ?? (b.total - b.taxes);
  const advanceDeposit = b.advance_deposit ?? fs.advance_deposit ?? 0;
  const balanceDue = b.balance_due_at_pickup ?? fs.balance_due_at_pickup ?? 0;
  const advancePct = fs.advance_deposit_pct ?? 0;
  const voucherMeta = fs.voucher;
  const voucherDiscount = b.voucher_discount || 0;
  const loyaltyMeta = fs.loyalty;
  const loyaltyDiscount = b.loyalty_discount || fs.loyalty_discount || 0;
  const originalTotal = b.total + voucherDiscount + loyaltyDiscount;

  return (
    <>
      <Row label={`Subtotal (${b.days} days)`} value={`${b.subtotal.toFixed(2)}`} />
      {b.addons_total > 0 && <Row label="Add-ons" value={`${b.addons_total.toFixed(2)}`} />}
      {(b.pickup_fee || 0) > 0 && (
        <Row label={`Pickup fee · ${b.pickup_location}`} value={`${b.pickup_fee.toFixed(2)}`} />
      )}
      {(b.return_fee || 0) > 0 && (
        <Row label={`Return fee · ${b.return_location}`} value={`${b.return_fee.toFixed(2)}`} />
      )}
      {(b.out_of_hours_fee || 0) > 0 && (
        <Row label="Out-of-hours fee" value={`${b.out_of_hours_fee.toFixed(2)}`} />
      )}
      {voucherDiscount > 0 && voucherMeta && (
        <div className="my-2 p-2 rounded bg-emerald-50 border border-emerald-200" data-testid="voucher-line">
          <div className="flex justify-between text-xs">
            <span className="text-emerald-800">
              {voucherMeta.is_automatic ? "Auto promo · " : "Code · "}
              <strong>{voucherMeta.code || b.voucher_code}</strong>
            </span>
            <span className="text-emerald-700 tabular-nums font-medium">
              −€{voucherDiscount.toFixed(2)}
            </span>
          </div>
        </div>
      )}
      {loyaltyDiscount > 0 && loyaltyMeta && (
        <div className="my-2 p-2 rounded bg-emerald-50 border border-emerald-200" data-testid="loyalty-line">
          <div className="flex justify-between text-xs">
            <span className="text-emerald-800">
              Loyalty · <strong>{loyaltyMeta.name}</strong> ({loyaltyMeta.discount_pct}% off)
            </span>
            <span className="text-emerald-700 tabular-nums font-medium">
              −€{loyaltyDiscount.toFixed(2)}
            </span>
          </div>
        </div>
      )}
      <div className="divider-line my-4"></div>

      {/* VAT informational — never added to total */}
      <div className="flex justify-between text-xs text-neutral-500 mb-1" data-testid="vat-info">
        <span>VAT ({vatRate}% included)</span>
        <span
          className="tabular-nums"
          data-testid="included-tax-amount"
        >
          €{(fs.included_tax_amount ?? b.taxes ?? 0).toFixed(2)}
        </span>
      </div>
      <div className="flex justify-between text-xs text-neutral-400 mb-3">
        <span>Net (excl. VAT)</span>
        <span className="tabular-nums">€{netTotal.toFixed(2)}</span>
      </div>

      {voucherDiscount > 0 ? (
        <div className="flex justify-between items-baseline mb-1" data-testid="total-with-discount">
          <span className="font-display text-2xl">Total</span>
          <div className="text-right">
            <span className="text-sm text-neutral-400 line-through mr-2 tabular-nums">
              €{originalTotal.toFixed(2)}
            </span>
            <span className="font-display text-2xl text-emerald-700 tabular-nums">
              €{b.total.toFixed(2)}
            </span>
          </div>
        </div>
      ) : (
        <Row label="Total" value={`${b.total.toFixed(2)}`} bold />
      )}

      {advancePct > 0 && (
        <div
          className="mt-4 p-3 border border-black/10 bg-[#EFECE6]/60 rounded space-y-1"
          data-testid="advance-deposit-notice"
        >
          <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1">
            Payment schedule
          </p>
          <div className="flex justify-between text-sm" data-testid="advance-payment-line">
            <span className="text-neutral-700">
              Advance Payment ({advancePct.toFixed(0)}%)
            </span>
            <span
              className="tabular-nums font-medium"
              data-testid="advance-payment-amount"
            >€{advanceDeposit.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm" data-testid="remaining-balance-line">
            <span className="text-neutral-700">Remaining Balance (Due at Pick-up)</span>
            <span
              className="tabular-nums font-medium"
              data-testid="remaining-balance-amount"
            >€{balanceDue.toFixed(2)}</span>
          </div>
          {b.payment_status === "partial" && (
            <div
              className="flex justify-between text-sm pt-2 mt-1 border-t border-black/10"
              data-testid="deposit-paid-line"
            >
              <span className="text-emerald-800 font-medium">Deposit paid</span>
              <span className="tabular-nums font-medium text-emerald-800">
                €{Number(b.paid_amount || advanceDeposit).toFixed(2)}
              </span>
            </div>
          )}
        </div>
      )}

      {(b.security_deposit || 0) > 0 && (
        <div
          className="mt-3 p-3 border border-black/10 bg-[#EFECE6]/60 rounded"
          data-testid="deposit-notice"
        >
          <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1">
            Refundable security deposit
          </p>
          <div className="flex justify-between text-sm">
            <span className="text-neutral-700">
              {(fs.security_deposit_amount || 0) > 0
                ? "Held on card at pickup (fixed €)"
                : `Held on card at pickup (${(fs.security_deposit_pct || 0).toFixed(0)}%)`}
            </span>
            <span className="tabular-nums font-medium">€{b.security_deposit.toFixed(2)}</span>
          </div>
        </div>
      )}
    </>
  );
}

function Row({ label, value, bold, discount }) {
  const tone = discount ? "text-emerald-700" : "text-neutral-700";
  return (
    <div className={`flex justify-between ${bold ? "font-display text-2xl" : `text-sm ${tone}`} mb-1`}>
      <span>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}
